//TODO: delete this file after new pricing migration
import Trans from 'next-translate/Trans';
import { Disclosure, Transition } from '@headlessui/react';
import clsx from 'clsx';

//hooks
import useTranslation from 'next-translate/useTranslation';
import useUser from '@/utils/hooks/swr/useUser';
import useFormatNumber from '@/hooks/format/useFormatNumber';

//components
import Icon from '@/components/base/Icon';

//types
import { CURRENCY_SYMBOLS } from '@/lib/billing/billing';
import type {
    SubscriptionOptionType,
    OptionsAvailableType
} from '@/hooks/swr/useBillingSubscription';

const PLAN_OPTIONS_MAPPING = {
    profiles: 'social_profiles',
    users: 'team_members',
    competitors: 'competitors',
    hashtags: 'hashtags',
    feeds: 'customized_feeds'
} as const;

const OptionWithIcon = ({ children }) => (
    <p className="flex items-center gap-4">
        <Icon
            icon="check"
            className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-icoBlue-100 text-xs text-icoBlue dark:bg-icoBlue/20"
        />
        {children}
    </p>
);

type PlanOptionProps = {
    optionKey: OptionsAvailableType;
    optionValues: SubscriptionOptionType;
    currency: keyof typeof CURRENCY_SYMBOLS;
    isManualPlan: boolean;
};
export const OldPlanOptions = ({
    optionKey,
    optionValues,
    currency,
    isManualPlan
}: PlanOptionProps) => {
    const { t } = useTranslation('settings');

    const { user } = useUser({
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
    });

    const userNumberFormat = user?.settings?.numbers_format ?? 'comma';
    const { formatPriceCurrencySymbol } = useFormatNumber(userNumberFormat);

    if (optionValues === null) {
        return (
            <OptionWithIcon>
                <span className="flex-1">
                    <Trans
                        i18nKey={`settings:subscription_invoices_page.unlimited_${PLAN_OPTIONS_MAPPING[optionKey]}`}
                        components={{ bold: <span className="font-GBold" /> }}
                    />
                </span>
                <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('subscription_invoices_page.included')}
                </span>
            </OptionWithIcon>
        );
    }

    if (!optionValues?.count_add_ons || isManualPlan) {
        return (
            <OptionWithIcon>
                <span className="flex-1">
                    <Trans
                        i18nKey={`settings:subscription_invoices_page.${PLAN_OPTIONS_MAPPING[optionKey]}`}
                        values={{ count: optionValues.count_total }}
                        components={{ bold: <span className="font-GBold" /> }}
                    />
                </span>
                <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                    {t('subscription_invoices_page.included')}
                </span>
            </OptionWithIcon>
        );
    }

    return (
        <Disclosure defaultOpen={true}>
            {({ open }) => (
                <>
                    <div>
                        <Disclosure.Button>
                            <OptionWithIcon>
                                <span className="flex-1">
                                    <Trans
                                        i18nKey={`settings:subscription_invoices_page.${PLAN_OPTIONS_MAPPING[optionKey]}`}
                                        values={{ count: optionValues?.count_total }}
                                        components={{
                                            bold: <span className="font-GBold text-icoBlue" />
                                        }}
                                    />
                                </span>
                                <Icon
                                    className={clsx(
                                        'text-icoGray-500 transition dark:text-icoDarkMode-wolf',
                                        {
                                            'rotate-180 transform': open
                                        }
                                    )}
                                    icon="chevron-bottom"
                                />
                            </OptionWithIcon>
                        </Disclosure.Button>
                    </div>
                    <Transition
                        enter="transition duration-100 ease-out"
                        enterFrom="transform scale-95 opacity-0"
                        enterTo="transform scale-100 opacity-100"
                        leave="transition duration-75 ease-out"
                        leaveFrom="transform scale-100 opacity-100"
                        leaveTo="transform scale-95 opacity-0">
                        <Disclosure.Panel>
                            <p className="mb-3 ml-12 flex justify-between">
                                <span>
                                    <Trans
                                        i18nKey={`settings:subscription_invoices_page.${PLAN_OPTIONS_MAPPING[optionKey]}`}
                                        values={{ count: optionValues?.count_included }}
                                        components={{ bold: <span className="font-GBold" /> }}
                                    />
                                </span>
                                <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    {t('subscription_invoices_page.included')}
                                </span>
                            </p>
                            <p className="ml-12 flex justify-between">
                                <span>
                                    <Trans
                                        i18nKey={`settings:subscription_invoices_page.add_ons`}
                                        values={{ count: optionValues?.count_add_ons }}
                                        components={{ bold: <span className="font-GBold" /> }}
                                    />
                                </span>
                                <span className="text-icoGray-500 dark:text-icoDarkMode-wolf">
                                    {t('subscription_invoices_page.price_per_month', {
                                        price: formatPriceCurrencySymbol(
                                            optionValues?.price_monthly *
                                                optionValues?.count_add_ons,
                                            currency
                                        )
                                    })}
                                </span>
                            </p>
                        </Disclosure.Panel>
                    </Transition>
                </>
            )}
        </Disclosure>
    );
};
